import { ApplicationStatus, GlobalErrorType, StepStatus } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import {
  determineNewDataPointsToShareForCardApplication,
  dispatchCardApplicationDataPointTrackingEvents,
} from '~/helpers/tracking.helper';
import {
  mapToCardApplicationDTO,
  mapToCardApplicationForm,
} from '~/mappers/DTO/cardApplication';
import {
  apiGetCardApplication,
  apiPatchCardApplication,
  apiPostCardApplication,
} from '~/services/api';
import { ApiStatus } from '~/store/types/sharedTypes';
import { AppDispatch, GetStateFunc } from '..';
import {
  clearCardApplicationState,
  goToNextStep,
  setApiStatus,
  setClientId,
  setDataPointsShared,
  updateApplicationStatus,
  updateCurrentOrganizationIdentifier,
  updateCurrentStep,
  updateForm,
} from '../slices/cardApplication.slice';
import { updateForm as updateLoanForm } from '../slices/loanApplication.slice';
import { toggleGlobalErrorOn } from '../slices/globalError.slice';
import { setUser } from '../slices/user.slice';
import { trackCardApplicationActions } from './analyticsActions';
import { pushToGtmLoanAction } from './gtmActions';

export const getCardApplication = (uuid: string) => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  dispatch(setApiStatus({ get: ApiStatus.Started }));
  const {
    intl: { market },
    cardApplication: { form },
    user: { authToken },
  } = getState();
  try {
    const clientApplicationData = await apiGetCardApplication(
      uuid,
      market,
      authToken
    );

    const mappedFormData = mapToCardApplicationForm(
      clientApplicationData,
      form
    );

    dispatch(updateForm(mappedFormData));
    dispatch(setApiStatus({ get: ApiStatus.Completed }));
    dispatch(
      updateCurrentOrganizationIdentifier(mappedFormData.organizationNumber)
    );

    if (clientApplicationData.clientId) {
      dispatch(setClientId(clientApplicationData.clientId));
    }

    if (clientApplicationData.finalized) {
      dispatch(updateApplicationStatus(ApplicationStatus.ApplicationFinished));
    } else {
      dispatch(updateCurrentStep(clientApplicationData.completedStep + 1));
    }

    dispatch(setDataPointsShared(clientApplicationData.dataPointsShared));
  } catch (err) {
    logToSentry(err, 'apiGetCardApplication', {
      applicationUuid: uuid,
    });
    dispatch(setApiStatus({ get: ApiStatus.Failed }));
    if (
      err.message === 'No ACCESS_TOKEN found!' ||
      err?.response?.status === 401
    ) {
      dispatch(toggleGlobalErrorOn(GlobalErrorType.TOKEN_EXPIRED));
    } else {
      dispatch(clearCardApplicationState());
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    }
  }
};

interface PostApplicationParams {
  shouldGoToNextStep?: boolean;
  finalized?: boolean;
  appliedForThroughLoanFlow?: boolean;
}

export const postCardApplication = ({
  shouldGoToNextStep = false,
  finalized = false,
  appliedForThroughLoanFlow = false,
}: PostApplicationParams) => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const {
    intl: { language, market },
    cardApplication: { form, currentStepNumber, dataPointsShared },
    user: { authToken, isAuthenticated },
  } = getState();
  const storedLandingUrl = sessionStorage.getItem('landingUrl') || '';
  const landingUrl = new URL(storedLandingUrl);
  landingUrl.searchParams.set('languageCode', language);
  const referenceUrl = sessionStorage.getItem('referrerUrl') || '';

  const dataPointsToShare = determineNewDataPointsToShareForCardApplication(
    form,
    dataPointsShared
  );

  dispatch(setDataPointsShared(dataPointsToShare));

  const {
    cardApplication: { dataPointsShared: updatedDataPointsShared },
  } = getState();

  const requestData = mapToCardApplicationDTO({
    form: {
      ...form,
      landingUrl: landingUrl.href,
      referenceUrl,
    },
    currentStepNumber,
    market,
    shouldPostApplication: true,
    finalized,
    currentStepCompleted: true,
    isAuthenticated,
    dataPointsShared: updatedDataPointsShared,
  });

  dispatch(setApiStatus({ post: ApiStatus.Started }));
  try {
    const result = await apiPostCardApplication(requestData, authToken);

    if (appliedForThroughLoanFlow) {
      dispatch(
        updateLoanForm({
          cardApplicationId: result.applicationId,
        })
      );
      dispatch(
        pushToGtmLoanAction({
          actionName: 'card_successfully_applied_for',
          extraContext: { cardApplicationId: result.applicationId },
        })
      );
    }

    dispatch(trackCardApplicationActions({ stepStatus: StepStatus.Finished }));

    dispatch(
      updateCurrentOrganizationIdentifier(
        requestData.organization.organizationNumber
      )
    );
    dispatch(
      updateForm({
        applicationUuid: result.applicationUuid,
        applicationId: result.applicationId,
        qCode: result.qCode,
        userCode: result.userCode,
      })
    );

    dispatchCardApplicationDataPointTrackingEvents(dispatch, dataPointsToShare);

    if (shouldGoToNextStep) {
      dispatch(goToNextStep());
    }

    if (result.accessToken) {
      document.cookie = `publicToken=${result.accessToken}; Max-Age=36000`;
      dispatch(setUser({ isAuthenticated, authToken: result.accessToken }));
    }

    dispatch(setApiStatus({ post: ApiStatus.Completed }));
  } catch (err) {
    dispatch(
      trackCardApplicationActions({
        stepStatus: StepStatus.Error,
        errorMessage: err?.response?.data?.message,
        extraContext: {
          context: 'postCardApplication',
        },
      })
    );
    logToSentry(err, 'apiPostCardApplication');
    dispatch(setApiStatus({ post: ApiStatus.Failed }));
    dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    throw err;
  }
};

interface PatchApplicationParams {
  finalized?: boolean;
  currentStepCompleted?: boolean;
  shouldGoToNextStep?: boolean;
}

export const patchCardApplication = ({
  finalized = false,
  currentStepCompleted = true,
  shouldGoToNextStep = false,
}: PatchApplicationParams = {}) => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const {
    cardApplication: { form, currentStepNumber, clientId, dataPointsShared },
    intl: { market },
    user: { authToken, isAuthenticated },
  } = getState();

  const dataPointsToShare = determineNewDataPointsToShareForCardApplication(
    form,
    dataPointsShared
  );

  dispatch(setDataPointsShared(dataPointsToShare));

  const {
    cardApplication: { dataPointsShared: updatedDataPointsShared },
  } = getState();

  const requestData = mapToCardApplicationDTO({
    form,
    currentStepNumber,
    market,
    shouldPostApplication: false,
    finalized,
    currentStepCompleted,
    clientId,
    isAuthenticated,
    dataPointsShared: updatedDataPointsShared,
  });

  dispatch(setApiStatus({ patch: ApiStatus.Started }));

  try {
    const patchApplicationData = await apiPatchCardApplication(
      requestData,
      authToken
    );
    dispatch(setApiStatus({ patch: ApiStatus.Completed }));

    if (currentStepCompleted) {
      dispatch(
        trackCardApplicationActions({ stepStatus: StepStatus.Finished })
      );
    }

    if (patchApplicationData.userCode) {
      dispatch(updateForm({ userCode: patchApplicationData.userCode }));
    }

    dispatchCardApplicationDataPointTrackingEvents(dispatch, dataPointsToShare);

    if (shouldGoToNextStep) {
      dispatch(goToNextStep());
    }

    if (finalized) {
      dispatch(updateApplicationStatus(ApplicationStatus.ApplicationFinished));
      dispatch(
        trackCardApplicationActions({ stepStatus: StepStatus.Submitted })
      );
    }
  } catch (err) {
    logToSentry(err, 'apiPatchCardApplication');
    if (
      finalized &&
      err?.response?.data?.message === 'Endpoint request timed out'
    ) {
      // If endpoint times out, we will assume this request updated the application in BO but we didn't get a response in time.
      dispatch(setApiStatus({ patch: ApiStatus.Completed }));
    } else {
      if (
        err.message === 'No ACCESS_TOKEN found!' ||
        err?.response?.status === 401
      ) {
        dispatch(setApiStatus({ patch: ApiStatus.Failed }));
        dispatch(toggleGlobalErrorOn(GlobalErrorType.TOKEN_EXPIRED));
      } else if (
        err?.response?.data?.errorCode === 'DuplicateGuarantorsDetected'
      ) {
        dispatch(setApiStatus({ patch: ApiStatus.Failed }));
        dispatch(toggleGlobalErrorOn(GlobalErrorType.CUSTOM_ERROR));
      } else {
        dispatch(updateApplicationStatus(ApplicationStatus.None));
        dispatch(setApiStatus({ patch: ApiStatus.Failed }));
        dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      }
      throw err;
    }
  }
};
